import { Button } from "src/components/ui/Button";
import { Facebook, Linkedin } from "lucide-react";
import { FcGoogle } from "react-icons/fc";
import { BsMicrosoft } from "react-icons/bs";
import { supabase } from "src/lib/supabaseClient";
import { useState } from "react";
import { useToast } from "src/components/ui/UseToast";
import { useAuthStore } from "src/store/authStore";

interface SocialLoginButtonsProps {
  isSubmitting: boolean;
  userType?: string; // Add this parameter
  acceptTerms?: boolean; // Add this parameter to check terms acceptance
  isSignup?: boolean; // Add this parameter to distinguish between signup and login
}

const SocialLoginButtons = ({
  isSubmitting,
  userType = "student", // Default to student if not provided
  acceptTerms = false, // Default to false if not provided
  isSignup = false, // Default to false (login mode)
}: SocialLoginButtonsProps) => {
  const { toast } = useToast();
  const { signInWithOAuth } = useAuthStore(); // Keep for other providers
  const [isLoading, setIsLoading] = useState<Record<string, boolean>>({});

  const handleSocialLogin = async (
    provider: "google" | "azure" | "facebook" | "linkedin_oidc"
  ) => {
    try {
      // Check if terms are accepted before proceeding (only for signup)
      if (isSignup && !acceptTerms) {
        toast({
          title: "Terms and Conditions Required",
          description: "Please accept the Terms of Service and Privacy Policy before signing up.",
          variant: "destructive",
        });
        return;
      }

      setIsLoading((prev) => ({ ...prev, [provider]: true }));

      // For now, only implement Google OAuth as it's the most commonly used
      // You can enable others in Supabase dashboard as needed
      if (provider !== "google") {
        toast({
          title: "Provider not available",
          description: `${provider} login is not configured yet. Please use Google or email login.`,
          variant: "destructive",
        });
        return;
      }

      // Use standard Supabase OAuth for both signup and login
      let redirectUrl;
      if (isSignup) {
        // For signup, include userType parameter
        redirectUrl = `${window.location.origin}/auth/callback?oauth_usertype=${userType}`;
      } else {
        // For login, use simple callback
        redirectUrl = `${window.location.origin}/auth/callback`;
      }

      const { error } = await supabase.auth.signInWithOAuth({
        provider,
        options: {
          redirectTo: redirectUrl,
        },
      });

      if (error) {
        throw error;
      }

      // If this code runs, redirectTo didn't immediately redirect
      toast({
        title: "Authentication initiated",
        description:
          "Please complete the authentication process in the new window.",
      });
    } catch (error: any) {
      console.error(`${provider} authentication error:`, error);
      toast({
        title: "Authentication failed",
        description: error.message || `Failed to authenticate with ${provider}`,
        variant: "destructive",
      });
    } finally {
      setIsLoading((prev) => ({ ...prev, [provider]: false }));
    }
  };

  return (
    <div className="flex justify-center mt-4">
      <Button
        type="button"
        variant="outline"
        onClick={() => handleSocialLogin("google")}
        disabled={isSubmitting || isLoading["google"] || (isSignup && !acceptTerms)}
        className={`flex items-center justify-center gap-2 w-full max-w-xs ${
          isSignup && !acceptTerms ? "opacity-50 cursor-not-allowed" : ""
        }`}
        title={isSignup && !acceptTerms ? "Please accept the Terms and Conditions first" : ""}
      >
        <FcGoogle size={20} />
        Google
      </Button>

      {/* Temporarily disable other providers until they're configured in Supabase */}
      {/* <Button
        type="button"
        variant="outline"
        disabled={true}
        className="flex items-center justify-center gap-2 opacity-50"
        title="Not available yet"
      >
        <BsMicrosoft size={20} className="text-blue-500" />
        Microsoft
      </Button>

      <Button
        type="button"
        variant="outline"
        disabled={true}
        className="flex items-center justify-center gap-2 opacity-50"
        title="Not available yet"
      >
        <Facebook size={20} className="text-blue-600" />
        Facebook
      </Button>

      <Button
        type="button"
        variant="outline"
        disabled={true}
        className="flex items-center justify-center gap-2 opacity-50"
        title="Not available yet"
      >
        <Linkedin size={20} className="text-blue-700" />
        LinkedIn
      </Button> */}
    </div>
  );
};

export default SocialLoginButtons;
